import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Camera, FileText } from 'lucide-react'
import { useFetchLocation } from '@/lib/hooks/server'
import { compressImage } from '@/utils/files'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Label } from '@/components/ui/label'

// Ajuste: comente corretamente ou alinhe o valor ao texto
const MAX_FILE_SIZE = 70 * 1024 * 1024 // ~70MB (ajuste a mensagem abaixo se quiser 70MB)
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png']
const ACCEPTED_DOC_TYPES = [...ACCEPTED_IMAGE_TYPES, 'application/pdf']

const isPdfFile = (file: unknown) =>
  file instanceof File && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))
const isAcceptedDocFile = (file: unknown) => file instanceof File && (ACCEPTED_IMAGE_TYPES.includes(file.type) || isPdfFile(file))

const solicitarPermissaoCamera = async () => {
  const isAndroid = /Android/i.test(navigator.userAgent)
  if (!isAndroid) return
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true })
    stream.getTracks().forEach(track => track.stop())
  } catch (err) {
    console.error('Erro ao solicitar câmera:', err)
  }
}

const imageFileSchema = z
  .any()
  .refine(f => typeof window !== 'undefined' && f instanceof File, 'Arquivo é obrigatório')
  .refine(f => f?.size <= MAX_FILE_SIZE, 'Arquivo deve ter no máximo 70MB')
  .refine(f => ACCEPTED_IMAGE_TYPES.includes(f?.type), 'Somente arquivos .jpg e .png são aceitos')

const documentFileSchema = z
  .any()
  .refine(f => typeof window !== 'undefined' && f instanceof File, 'Arquivo é obrigatório')
  .refine(f => f?.size <= MAX_FILE_SIZE, 'Arquivo deve ter no máximo 70MB')
  .refine(f => isAcceptedDocFile(f), 'Somente arquivos .jpg, .png e .pdf são aceitos')

export const photoCaptureSchema = z.object({
  fotoPessoa: imageFileSchema,
  fotoDoc: documentFileSchema
})

export type PhotoCaptureFormData = z.infer<typeof photoCaptureSchema>

export interface CapturedData {
  fotoPessoa: File
  fotoDoc: File
  local: string
  maquina: string
  currentDateTime: string
}

interface PhotoCaptureProps {
  onComplete: (data: CapturedData) => void
  user?: boolean
}

export default function PhotoCapture({ onComplete }: PhotoCaptureProps) {
  const [personPhotoUrl, setPersonPhotoUrl] = useState<string | null>(null)
  const [documentPhotoUrl, setDocumentPhotoUrl] = useState<string | null>(null)
  const [documentFileName, setDocumentFileName] = useState<string | null>(null)
  const [location, setLocation] = useState<string>('Carregando...')
  const [deviceInfo, setDeviceInfo] = useState<string>('')
  const [currentDateTime, setCurrentDateTime] = useState<string>('')

  const form = useForm<PhotoCaptureFormData>({
    resolver: zodResolver(photoCaptureSchema),
    mode: 'onChange'
  })

  useEffect(() => {
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true

    if (isInStandaloneMode) {
      alert(
        'O acesso à localização pode estar bloqueado neste modo (atalho/PWA). Para usar todos os recursos, abra no navegador como o Chrome.'
      )
    }
    const now = new Date()
    setCurrentDateTime(now.toLocaleString())
    setDeviceInfo(navigator.userAgent || 'Informações não disponíveis')
  }, [])

  const { data: locationData, error: locationError } = useFetchLocation()

  useEffect(() => {
    if (locationData && typeof locationData === 'object' && 'city' in locationData && 'region' in locationData) {
      setLocation(
        `${locationData.city}, ${locationData.region}, ${locationData.country}, ${locationData.zip} (lat: ${locationData.lat}, lon: ${locationData.lon})`
      )
    } else if (locationError) {
      setLocation('Erro ao obter localização.')
    } else {
      setLocation('Localização não disponível.')
    }
  }, [locationData, locationError])

  const onSubmit = (data: PhotoCaptureFormData) => {
    const capturedData: CapturedData = {
      fotoPessoa: data.fotoPessoa as File,
      fotoDoc: data.fotoDoc as File,
      local: location,
      maquina: deviceInfo,
      currentDateTime
    }
    onComplete(capturedData)
  }

  const handlePhotoChange = async (
    file: File,
    field: 'fotoPessoa' | 'fotoDoc',
    setUrl: (u: string | null) => void,
    options?: { allowPdf?: boolean }
  ) => {
    if (!file) return
    if (options?.allowPdf && isPdfFile(file)) {
      form.setValue(field, file, { shouldValidate: true, shouldDirty: true })
      setUrl(null)
      setDocumentFileName(file.name)
      return
    }

    try {
      const compressed = await compressImage(file, 1024, 0.7)
      const imageUrl = URL.createObjectURL(compressed)
      form.setValue(field, compressed as File, { shouldValidate: true, shouldDirty: true })
      setUrl(imageUrl)
      if (field === 'fotoDoc') setDocumentFileName(null)
    } catch (error) {
      console.error('Erro ao comprimir imagem:', error)
      alert('Erro ao processar a imagem. Tente novamente.')
    }
  }

  /**
   * Abre um input file dinâmico.
   * - Se capture === 'environment', sugere uso da câmera (quando suportado)
   * - Se capture === undefined, abre galeria/arquivos normalmente
   */
  const openFilePicker = (
    onPicked: (file: File) => void,
    options?: { capture?: 'environment' | 'user'; accept?: string }
  ) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = options?.accept ?? ACCEPTED_IMAGE_TYPES.join(',')
    if (options?.capture) {
      // Sugerir câmera (environment = traseira, user = frontal)
      ;(input as any).capture = options.capture
    }
    input.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) onPicked(file)
      // limpa o input p/ permitir escolher o mesmo arquivo de novo
      input.value = ''
    }
    input.click()
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-64 shadow-xl">
      <div className="bg-background p-6 rounded-lg shadow-md w-full max-w-2xl">
        <h2 className="text-2xl font-bold mb-6 text-center">Captura de Fotos e Informações</h2>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* FOTO PESSOA */}
              <FormField
                control={form.control}
                name="fotoPessoa"
                render={({ field: { onChange } }) => (
                  <FormItem>
                    <FormLabel>Pessoa com Documento em mãos</FormLabel>
                    <FormControl>
                      <div className="relative h-48 bg-gray-200 rounded-lg overflow-hidden">
                        {personPhotoUrl ? (
                          <img src={personPhotoUrl} alt="Pessoa documento" className="w-full h-full object-cover" />
                        ) : (
                          <img
                            src="/pessoaDocs.png"
                            alt="Pessoa documento padrão"
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute bottom-2 right-2 flex gap-2">
                          {/*  */}
                          <Button
                            type="button"
                            size="sm"
                            onClick={async () => {
                              await solicitarPermissaoCamera()
                              openFilePicker(
                                file => {
                                  handlePhotoChange(file, 'fotoPessoa', setPersonPhotoUrl)
                                  onChange(file)
                                },
                                { capture: 'environment' }
                              )
                            }}
                          >
                            <Camera className="w-4 h-4" />
                            Câmera
                          </Button>
                          {/* Escolher arquivo (galeria/arquivos) */}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              openFilePicker(file => {
                                handlePhotoChange(file, 'fotoPessoa', setPersonPhotoUrl)
                                onChange(file)
                              })
                            }}
                          >
                            Escolher arquivo
                          </Button>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* FOTO DOCUMENTO */}
              <FormField
                control={form.control}
                name="fotoDoc"
                render={({ field: { onChange } }) => (
                  <FormItem>
                    <FormLabel>Documento mostrando a assinatura</FormLabel>
                    <FormControl>
                      <div className="relative h-48 bg-gray-200 rounded-lg overflow-hidden">
                        {documentPhotoUrl ? (
                          <img src={documentPhotoUrl} alt="Documento" className="w-full h-full object-cover" />
                        ) : documentFileName ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-muted text-foreground p-3 text-center">
                            <FileText className="w-10 h-10 mb-2" />
                            <span className="text-sm font-medium break-all">{documentFileName}</span>
                          </div>
                        ) : (
                          <img src="/docs.png" alt="Documento padrão" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute bottom-2 right-2 flex gap-2">
                          {/*  */}
                          <Button
                            type="button"
                            size="sm"
                            onClick={async () => {
                              await solicitarPermissaoCamera()
                              openFilePicker(
                                file => {
                                  handlePhotoChange(file, 'fotoDoc', setDocumentPhotoUrl)
                                  onChange(file)
                                },
                                { capture: 'environment', accept: ACCEPTED_IMAGE_TYPES.join(',') }
                              )
                            }}
                          >
                            <Camera className="w-4 h-4" />
                            Câmera
                          </Button>
                          {/* Escolher arquivo */}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              openFilePicker(file => {
                                handlePhotoChange(file, 'fotoDoc', setDocumentPhotoUrl, { allowPdf: true })
                                onChange(file)
                              }, { accept: `${ACCEPTED_DOC_TYPES.join(',')},.pdf` })
                            }}
                          >
                            Escolher arquivo
                          </Button>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <Label className="font-semibold">Data e Hora:</Label>
                <p>{currentDateTime}</p>
              </div>
              <div>
                <Label className="font-semibold">Localização:</Label>
                <p>
                  {locationData && typeof locationData === 'object' && 'city' in locationData
                    ? location
                    : locationError
                      ? 'Erro ao obter localização.'
                      : 'Localização não disponível'}
                </p>
              </div>

              <div>
                <Label className="font-semibold">Informações do Dispositivo:</Label>
                <p className="text-sm break-words">{deviceInfo}</p>
              </div>
            </div>

            <Button type="submit" disabled={!form.formState.isValid || form.formState.isSubmitting} className="w-full">
              {form.formState.isSubmitting ? 'Enviando...' : 'Assinar'}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}
